import axios from "axios";
import { cloneDeep } from "lodash";
import { toast } from "react-toastify";
import { addFolderTree, findFolder, hasDuplicateFolder, isDefaultDraftReportsPath, isValidFolderName, normalizeFolderPath, removeFolderTree, renameFolderTree, sanitizeFolderName } from "./folderTree";
import { forceRefreshReports } from "../../components/reports/components/reportList/reducers/reportListReducer";

/* -------------------- MODULE-LEVEL FLAG -------------------- */
// Used to prevent duplicate calls for folder list
let isFoldersLoading = false;
let isFoldersLoaded = false;

/* -------------------- INITIAL STATE -------------------- */
const INITIAL_STATE = {
  loading: true,
  error: null,
  folders: [],
  searchFolders: [],
  modalFolders: [],
  modalSearchFolders: [],
  sasToken: "",
  selected: [],
  forceLoad: false,
  mutationPending: false,
};

/* -------------------- ACTION TYPES -------------------- */
const FETCH_FOLDER_LIST_BEGIN = "FETCH_FOLDER_LIST_BEGIN";
const FETCH_FOLDER_LIST_SUCCESS = "FETCH_FOLDER_LIST_SUCCESS";
const FETCH_FOLDER_LIST_FAILURE = "FETCH_FOLDER_LIST_FAILURE";
const SET_SEARCH_FOLDERS = "SET_SEARCH_FOLDERS";
const SET_MODAL_SEARCH_FOLDERS = "SET_MODAL_SEARCH_FOLDERS";
const SET_FORCE_LOAD = "SET_FORCE_LOAD";
const SET_BASE_FOLDERS = "SET_BASE_FOLDERS";
const SET_SELECTED_FOLDERS = "SET_SELECTED_FOLDERS";
const SET_FOLDER_MUTATION = "SET_FOLDER_MUTATION";

/* -------------------- ACTION CREATORS -------------------- */

const fetchFolderListBegin = () => ({
  type: FETCH_FOLDER_LIST_BEGIN,
});
const fetchFolderListSuccess = (folders) => {
  return { type: FETCH_FOLDER_LIST_SUCCESS, payload: folders };
};
const setSearchFolders = (folders) => {
  return { type: SET_SEARCH_FOLDERS, payload: folders };
};
const setModalSearchFolders = (folders) => {
  return { type: SET_MODAL_SEARCH_FOLDERS, payload: folders };
};

const fetchFolderListError = (error) => ({
  type: FETCH_FOLDER_LIST_FAILURE,
  payload: { error },
});

/* -------------------- ASYNC ACTION CREATORS -------------------- */
export const getFolderList = () => {
  return (dispatch) => {
    // Prevent duplicate calls
    if (isFoldersLoading || isFoldersLoaded) {
      return;
    }
    isFoldersLoading = true;
    dispatch(fetchFolderListBegin());
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/getReportFolders`,
        {
          // client
          //idToken: localStorage.getItem("id_token")
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        // let reports = response.data.workspaces.map(item => {
        //   return item.reports;
        // });
        let workspaces = response.data.workspaces.filter((item) => {
          return !item.hidden;
        });
        isFoldersLoading = false;
        isFoldersLoaded = true;
        dispatch(fetchFolderListSuccess(workspaces));
      })
      .catch((error) => {
        isFoldersLoading = false;
        dispatch(fetchFolderListError(error));
      });
  };
};
export const getFolderListNoLoading = () => {
  return (dispatch) => {
    // Prevent duplicate calls
    if (isFoldersLoading || isFoldersLoaded) {
      return;
    }
    isFoldersLoading = true;
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/getReportFolders`,
        {
          // client
          //idToken: localStorage.getItem("id_token")
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        let workspaces = response.data.workspaces.filter((item) => {
          return !item.hidden;
        });
        isFoldersLoading = false;
        isFoldersLoaded = true;
        dispatch(fetchFolderListSuccess(workspaces));
        dispatch(setForceLoad(true));
      })
      .catch((error) => {
        isFoldersLoading = false;
        dispatch(fetchFolderListError(error));
      });
  };
};
export const createFolder = (folder, ws) => {
  return () => {
    return axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/createFolder`,
        {
          folder,
          ws,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        if (response.data?.success === false) throw new Error("Failed to create folder");
        return response;
      });
  };
};
export const saveFolder = (oldPath, input, isNew = false) => {
  return async (dispatch, getState) => {
    const state = getState().pbiMgmt;
    if (state.mutationPending) {
      toast.error("Please wait for the current folder change to finish.");
      return false;
    }
    const name = sanitizeFolderName(input);
    const oldBase = normalizeFolderPath(oldPath);
    const parts = oldBase.split("/");
    if (parts.length < 2 || !isValidFolderName(name)) {
      toast.error("Folder name is invalid.");
      return false;
    }
    if (!isNew && isDefaultDraftReportsPath(oldPath)) {
      toast.error("The Draft Reports folder cannot be renamed.");
      return false;
    }
    if (hasDuplicateFolder(state.folders, oldPath, name)) {
      toast.error("A folder with that name already exists here.");
      return false;
    }
    if (!isNew && findFolder(state.folders, oldPath)?.name === name) return true;
    const parentPath = parts.slice(0, -1).join("/");
    const newPath = `${parentPath}/${name}/`;
    if (!isNew && isDefaultDraftReportsPath(newPath)) {
      toast.error("The Draft Reports folder cannot be renamed.");
      return false;
    }
    const keys = ["folders", "searchFolders", "modalFolders", "modalSearchFolders"];
    const previous = Object.fromEntries(keys.map((key) => [key, state[key]]));
    const updated = Object.fromEntries(keys.map((key) => [key,
      isNew ? addFolderTree(state[key], parentPath, name) : renameFolderTree(state[key], oldPath, newPath),
    ]));
    dispatch({ type: SET_FOLDER_MUTATION, payload: { ...updated, mutationPending: true } });
    try {
      if (isNew) {
        await dispatch(createFolder(newPath, parts[0]));
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/renameFolder`,
          { oldPath: `${oldBase}/`, newPath },
          { headers: { ClientToken: localStorage.getItem("clientToken") } },
        );
        if (response.data?.success === false) throw new Error("Failed to rename folder");
      }
    } catch {
      dispatch({ type: SET_FOLDER_MUTATION, payload: { ...previous, mutationPending: false } });
      toast.error(isNew ? "Failed to create folder." : "Failed to rename folder.");
      return false;
    }
    dispatch({ type: SET_FOLDER_MUTATION, payload: { mutationPending: false } });
    dispatch(forceRefreshReports());
    toast.success(isNew ? "Folder created." : "Folder name updated.");
    return true;
  };
};
export const deleteFolder = (path) => {
  return async (dispatch, getState) => {
    const state = getState().pbiMgmt;
    if (state.mutationPending) {
      toast.error("Please wait for the current folder change to finish.");
      return false;
    }
    const folder = findFolder(state.folders, path);
    if (!folder || folder.fileType !== "folder" || folder.children.length > 0) {
      toast.error("Only empty folders can be deleted.");
      return false;
    }

    const keys = ["folders", "searchFolders", "modalFolders", "modalSearchFolders"];
    const previous = Object.fromEntries(keys.map((key) => [key, state[key]]));
    const updated = Object.fromEntries(keys.map((key) => [key, removeFolderTree(state[key], path)]));
    dispatch({ type: SET_FOLDER_MUTATION, payload: { ...updated, mutationPending: true } });
    const toastId = toast.loading("Deleting folder...");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/deleteFolder`,
        {
          path,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      );
      if (response.data?.success === false) throw new Error("Failed to delete folder");
    } catch {
      dispatch({ type: SET_FOLDER_MUTATION, payload: { ...previous, mutationPending: false } });
      toast.update(toastId, {
        render: "Failed to delete folder.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      return false;
    }

    dispatch({ type: SET_FOLDER_MUTATION, payload: { mutationPending: false } });
    toast.update(toastId, {
      render: "Folder deleted.",
      type: "success",
      isLoading: false,
      autoClose: 5000,
    });
    dispatch(forceRefreshReports());
    return true;
  };
};
export const setForceLoad = (status) => {
  return (dispatch) => {
    dispatch({ type: SET_FORCE_LOAD, payload: status });
  };
};
export const moveReport = (rpt, target) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/moveReport`,
        {
          rpt,
          target,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      );
      if (response.data?.success === false) throw new Error("Failed to move report");
      dispatch(getFolderList());
      // _reportsLoaded is still true at this point so getReports() would be a
      // no-op. forceRefreshReports resets the flag before dispatching so the
      // new folder assignment is reflected immediately on next folder navigation.
      dispatch(forceRefreshReports());
      toast.success("Report Moved");
      return true;
    } catch {
      toast.error("Move Failed");
      return false;
    }
  };
};
export const filterPBIFolders = (filter, folders, modal = false) => {
  return (dispatch) => {
    if (filter === "") {
      if (modal) {
        dispatch(setModalSearchFolders(folders));
      } else {
        dispatch(setSearchFolders(folders));
      }
    } else {
      let newFolders = cloneDeep(folders);
      if (modal) {
        dispatch(setModalSearchFolders(searchFilter(newFolders, filter, true)));
      } else {
        dispatch(setSearchFolders(searchFilter(newFolders, filter, false)));
      }
    }
  };
};
export const selectFolders = (folders, select) => {
  return { type: SET_SELECTED_FOLDERS, folders, select };
};

export const setFolderList = (folders) => {
  return (dispatch) => {
    let sFolder = folders;
    if (typeof folders === "object") {
      sFolder = folders.treeData;
    }
    if (sFolder !== undefined && sFolder !== null) {
      let workspaces = sFolder.filter((item) => {
        return !item.hidden;
      });
      dispatch(fetchFolderListSuccess(workspaces));
    }
    //dispatch(setSearchFolders(folders.treeData));
  };
};
export const setBaseFolders = (folders) => {
  return (dispatch) => {
    dispatch({ type: SET_BASE_FOLDERS, payload: folders });
  };
};

function removeFiles(filterFolders) {
  filterFolders.forEach((item) => {
    if (item.children) {
      item.children = item.children.filter((child) => child.fileType === "folder");
      removeFiles(item.children);
    }
  });
}
function searchFilter(newFolders, filter, modal) {
  return newFolders.map((newFolder) => {
    if (newFolder.children === undefined) {
      newFolder.reports = newFolder.reports.filter((report) => {
        return newFolder.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
      });
    } else {
      newFolder.children = newFolder.children.filter((folder, index) => {
        if (folder.children === undefined) {
          return folder.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
        } else {
          folder.children = folder.children.filter((child) => {
            if (child.fileType === "folder") {
              if (modal) {
                return child.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
              } else {
                child.children = child.children.filter((gChild) => {
                  return gChild.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
                });
                if (child.children.length > 0) {
                  return true;
                } else {
                  return false;
                }
              }
            } else {
              return child.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
            }
          });
          if (folder.children.length > 0 || folder.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1) {
            return true;
          } else {
            return false;
          }
        }
      });
    }
    return newFolder;
  });
}

/* -------------------- REDUCER -------------------- */
export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case SET_FOLDER_MUTATION:
      return { ...state, ...action.payload };
    case FETCH_FOLDER_LIST_BEGIN:
      return { ...state, loading: true, error: null };
    case FETCH_FOLDER_LIST_SUCCESS:
      let fFolders = cloneDeep(action.payload);
      if (!Array.isArray(action.payload)) {
        fFolders = fFolders.treeData;
        console.log("IT IS TREE NODE");
      }

      removeFiles(fFolders);

      if (Array.isArray(action.payload)) {
        return {
          ...state,
          folders: action.payload,
          searchFolders: action.payload,
          modalFolders: fFolders,
          modalSearchFolders: fFolders,
          loading: false,
        };
      } else {
        return {
          ...state,
          folders: action.payload.treeData,
          searchFolders: action.payload.treeData,
          modalFolders: fFolders,
          modalSearchFolders: fFolders,
          loading: false,
        };
      }

    case FETCH_FOLDER_LIST_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
        reportConfig: [],
      };
    case SET_SEARCH_FOLDERS:
      return {
        ...state,
        searchFolders: action.payload,
      };
    case SET_MODAL_SEARCH_FOLDERS:
      return {
        ...state,
        modalSearchFolders: action.payload,
      };

    case SET_BASE_FOLDERS:
      return {
        ...state,
        modalFolders: action.payload,
        modalSearchFolders: action.payload,
      };
    case SET_FORCE_LOAD:
      return { ...state, forceLoad: action.payload };
    case SET_SELECTED_FOLDERS:
      let newSelected = cloneDeep(state.selected);
      let newFolders = cloneDeep(state.folders);
      newFolders = newFolders.map((folder) => {
        if (folder.name === action.folders.name) {
          folder.selected = action.select;
        }
        return folder;
      });
      action.folders.selected = action.select;
      if (action.select) {
        newSelected.push(action.folders);
      } else {
        if (Array.isArray(action.folders)) {
          let aFolders = action.folders.map((folder) => {
            return folder.path;
          });
          newSelected = newSelected.filter((folder) => {
            return aFolders.indexOf(folder.path) === -1;
          });
        } else {
          newSelected = newSelected.filter((folder) => {
            return folder.path !== action.folders.path;
          });
        }
      }
      return {
        ...state,
        selected: newSelected,
        folders: newFolders,
      };
    default:
      return state;
  }
};
